import * as React from 'react';
import { CustomButton, ButtonType, Modal, ModalSize, ModalBody, ModalFooter, Form, Select, TextInput, AlertMessage, AlertType, CustomSubmitButton } from "soprasteria-ui-library";
import { useAuth } from '../../../../App';
import { InvoiceApi } from "../../../../api/InvoiceApi";
import { ICreateInvoice, InvoiceDataInformation } from '../../../../models/IInvoice';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { setMessage } from '../../../../helpers/MessageHelper';
import { CreateInvoiceInvoicesTable } from './CreateInvoiceInvoicesTable';
import "./CreateInvoiceModal.scss";
import { dateTimeToDateString } from '../../../../helpers/DateHelper';
import { formatCurrency } from '../../../../helpers/CurrencyHelper';

interface ICreateInvoiceModal {
    renderForm: boolean;
    setRenderForm: React.Dispatch<React.SetStateAction<boolean>>;
    changeSuccessMessage: (message: string) => void;
}

export const CreateInvoiceModal: React.FunctionComponent<ICreateInvoiceModal> = ({ setRenderForm, renderForm, changeSuccessMessage }) => {
    const auth = useAuth();
    const api = new InvoiceApi(auth);
    const [invoiceDataInformation, setInvoiceDataInformation] = React.useState<InvoiceDataInformation>()
    const [treatmentTypes, setTreatmentTypes] = React.useState<IListItem[]>()
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>();
    const [selectedTreatmentType, setSelectedTreatmentType] = React.useState<IListItem>();
    const [invoiceReference, setInvoiceReference] = React.useState<string>();

    React.useEffect(() => {
        getTreatmentTypes();
    }, [])

    const getTreatmentTypes = () => {
        api.getTreatmentTypesForCreateInvoice((data) => {
            const treatmentTypesListItems = data.map<IListItem>((treatmentType) => { return { value: treatmentType.treatmentTypeId.toString(), text: treatmentType.description } });
            if (treatmentTypesListItems.length === 1) {
                setSelectedTreatmentType(treatmentTypesListItems[0]);
            }
            setTreatmentTypes(treatmentTypesListItems);
        });
    }

    const getInvoiceData = (data: ICreateInvoice) => {
        setIsSubmitting(true);
        api.getInvoiceData(data, (response) => {
            setErrorMessage(undefined);
            if (response.errorMessage) {
                setErrorMessage(response.errorMessage);
            }
            else {
                setInvoiceDataInformation(response);
                setInvoiceReference(data.invoiceReference);
            }
        }, (message) => {
            const newMessage = setMessage(message, errorMessage ?? "");
            setErrorMessage(newMessage);
        }, () => setIsSubmitting(false));
    }

    const createInvoices = (data: ICreateInvoice) => {
        if (invoiceReference && invoiceDataInformation) {
            setIsSubmitting(true);
            data.invoiceReference = invoiceReference;
            data.careProviderIds = invoiceDataInformation.invoiceData.map((invoice) => { return invoice.careProvider.careProviderId })
            api.createInvoices(data, () => {
                if (invoiceDataInformation.invoiceData && invoiceDataInformation.invoiceData.length > 1) {
                    changeSuccessMessage("Fakturor har skapats");
                }
                else {
                    changeSuccessMessage("Fakturan har skapats");
                }
                document.getElementById("close-create-invoice-modal")?.click();
            }, (error) => {
                setErrorMessage(error);
            }, () => setIsSubmitting(false));
        }
    }

    const changeSelectedTreatmentType = (treatmentTypeId: string) => {
        const treatmentType = treatmentTypes?.find((treatment) => treatment.value === treatmentTypeId);
        setSelectedTreatmentType(treatmentType);
    }

    return (
        <Modal modalSize={ModalSize.extraLarge} id="create-invoice-modal" header="Fakturering">
            {
                renderForm
                    ?
                    <Form >
                        <div className="create-invoice-modal-content">
                            <ModalBody>
                                <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                                <div className="row">
                                    {
                                        invoiceDataInformation ?
                                            <>
                                                <div className="col-6">
                                                    <TextInput name="organisationReadonly" label="Organisation" inlineLabel readonly defaultValue={invoiceDataInformation.careProvider.displayOrganisationInformation} />
                                                </div>
                                                <div className="col-6">
                                                </div>
                                            </>
                                            : null
                                    }
                                    <div className="col-6">
                                        <Select label="Fakturatyp" name="treatmentTypeId" options={treatmentTypes ?? []} isLoading={treatmentTypes === undefined} selectedValue={selectedTreatmentType?.value} onChange={(val) => changeSelectedTreatmentType(val)} className={invoiceDataInformation ? "d-none" : ""} inlineLabel required />
                                        {
                                            invoiceDataInformation ?
                                                <TextInput label="Fakturatyp" name="treatmentTypeReadonly" defaultValue={invoiceDataInformation?.treatmentType} inlineLabel readonly />
                                                : null
                                        }
                                    </div>
                                    <div className="col-6">
                                        <TextInput name="invoiceReference" label="Er fakturareferens" required inlineLabel maxLength={20} readonly={invoiceDataInformation !== undefined} defaultValue={invoiceReference} />
                                    </div>
                                    {
                                        invoiceDataInformation ?
                                            <>
                                                <div className="col-6">
                                                    <TextInput name="invoiceDateReadonly" label="Fakturadatum" inlineLabel readonly defaultValue={dateTimeToDateString(invoiceDataInformation.invoiceDate)} />
                                                </div>
                                                <div className="col-6">
                                                    <TextInput name="totalBillableAmountReadonly" label="Totalt belopp att fakturera" inlineLabel readonly defaultValue={formatCurrency(invoiceDataInformation.totalBillableAmount)} />
                                                </div>
                                            </>
                                            : null
                                    }
                                    <div className={invoiceDataInformation ? "col-12" : "d-none"}>
                                        {
                                            invoiceDataInformation?.invoiceData && invoiceReference ?
                                                <CreateInvoiceInvoicesTable invoiceReference={invoiceReference} invoices={invoiceDataInformation?.invoiceData} selectedTreatmentType={selectedTreatmentType?.value} />
                                                : null
                                        }
                                    </div>
                                </div>
                            </ModalBody>
                        </div>
                        <ModalFooter>
                            <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-create-invoice-modal"
                                onClick={() => {
                                    setRenderForm(false);
                                    setInvoiceDataInformation(undefined);
                                    setSelectedTreatmentType(undefined);
                                    setInvoiceReference(undefined);
                                    setErrorMessage(undefined);
                                }}
                            />
                            <CustomSubmitButton onButtonSubmit={invoiceDataInformation ? createInvoices : getInvoiceData} isLoading={isSubmitting}>{invoiceDataInformation ? "Fakturera" : "Hämta fakturaunderlag"}</CustomSubmitButton>
                        </ModalFooter>
                    </Form>
                    : null
            }
        </Modal>
    )
}